import { useEffect, useState } from 'react';
import { Platform } from 'react-native';

export function useServiceWorker() {
    const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
    const [newVersionAvailable, setNewVersionAvailable] = useState(false);

    useEffect(() => {
        if (Platform.OS !== 'web' || !('serviceWorker' in navigator)) {
            return;
        }

        const registerSW = async () => {
            let registration: ServiceWorkerRegistration;
            try {
                const swUrl = '/sw.js';
                console.log('[SW] Attempting registration...');
                
                registration = await navigator.serviceWorker.register(swUrl);
                console.log('[SW] Registered successfully.');

                // Force a check on every page load
                registration.update().catch(err => {
                    console.warn('[SW] Initial update check failed:', err.message);
                });

                const onInstalled = (worker: ServiceWorker) => {
                    if (worker.state === 'installed' && navigator.serviceWorker.controller) {
                        console.log('[SW] New version installed and waiting.');
                        setWaitingWorker(worker);
                        setNewVersionAvailable(true);
                    }
                };

                // Check for waiting worker (if we refreshed while one was already there)
                if (registration.waiting) {
                    onInstalled(registration.waiting);
                }

                // Listen for new workers being installed
                registration.onupdatefound = () => {
                    const newWorker = registration.installing;
                    if (newWorker) {
                        newWorker.onstatechange = () => {
                            if (newWorker.state === 'installed') {
                                onInstalled(newWorker);
                            }
                        };
                    }
                };

                // Check for updates every 30 seconds
                const intervalId = setInterval(() => {
                    if (navigator.onLine) {
                        console.log('[SW] Checking for updates (30-second interval)...');
                        registration.update().catch(err => {
                            console.warn('[SW] Update check failed:', err.message);
                        });
                    }
                }, 30000);

                return intervalId;

            } catch (error) {
                console.error('[SW] Registration failed:', error);
                return null;
            }
        };

        const initPromise = registerSW();

        // Handle controllerchange (reload after skipWaiting)
        let refreshing = false;
        const handleControllerChange = () => {
            if (refreshing) return;
            refreshing = true;
            console.log('[SW] Controller changed. Reloading page...');
            window.location.reload();
        };

        navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

        return () => {
            initPromise.then(intervalId => {
                if (intervalId) clearInterval(intervalId);
            });
            navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
        };

    }, []);

    const updateServiceWorker = () => {
        if (waitingWorker) {
            waitingWorker.postMessage({ type: 'SKIP_WAITING' });
        }
    };

    return {
        newVersionAvailable,
        updateServiceWorker,
    };
}
